import { useCallback, useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { useConfirmation } from 'ui/hooks'
import { ClientUserListResponseItem } from '~bank-client-users/types/ClientUserListResponseItem'

import { handlerServerError } from '@orbit/errors'

export const useClientUserEdit = () => {
  const queryClient = useQueryClient()

  const [isEditClientUserDialogOpen, setIsEditClientUserDialogOpen] = useState<boolean>(false)
  const [clientUserForEditing, setClientUserForEditing] = useState<ClientUserListResponseItem | undefined>(undefined)

  const openEditClientUserDialog = () => setIsEditClientUserDialogOpen(true)
  const closeEditClientUserDialog = () => {
    setClientUserForEditing(undefined)
    setIsEditClientUserDialogOpen(false)
  }

  const [clientUserForDeleting, setClientUserForDeleting] = useState<ClientUserListResponseItem | undefined>(undefined)

  const { mutate: removeClientUser } = useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => Request.delete(Request.urls.bank.removeClientUser, { id }),
    onSuccess: async () => {
      Toast.successSmall('Client user was successfully removed!')
      queryClient.invalidateQueries({ stale: true })
    },
    onError: (error) => handlerServerError(error)
  })

  const { Confirmation: RemoveClientUserConfirmation, open } = useConfirmation({
    message: `Are you sure you want to remove? 
      User will lose access to all companies.`,
    onConfirm: () => (clientUserForDeleting ? removeClientUser({ id: clientUserForDeleting.id }) : null),
    confirmationButtonText: 'Confirm',
    isConfirmationNegative: true
  })

  const openRemoveClientUserConfirmation = useCallback(
    (item: ClientUserListResponseItem) => {
      setClientUserForDeleting(item)
      open()
    },
    [setClientUserForDeleting]
  )

  const { mutate: sendInviteLink } = useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => Request.post(Request.urls.bank.sendInviteLinkClientUser, { id }),
    onSuccess: async () => Toast.successSmall('Invite Link was successfully sent!'),
    onError: (error) => handlerServerError(error)
  })

  return {
    isEditClientUserDialogOpen,
    openEditClientUserDialog,
    closeEditClientUserDialog,
    clientUserForEditing,
    setClientUserForEditing,
    RemoveClientUserConfirmation,
    openRemoveClientUserConfirmation,
    sendInviteLink
  }
}
